import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import * as Locales from 'date-fns/locale';
import { getFormattedDate } from 'lib/common';
import {
  EmptyIndicator,
  LoadingIndicator,
  Pagination
} from 'components/common';
import { CalendarEventCalendarListItem } from '.';

import type { CalendarEventExtended } from 'shared-types';

interface Props {
  getEvents: () => Promise<{
    events: CalendarEventExtended[];
    pages?: number;
  }>;
  onEventClick?: (event: CalendarEventExtended) => void;
}

const CalendarEventCalendarListView = ({ getEvents, onEventClick }: Props) => {
  const { language, messages, pageState, theme } = useReduxState();

  const router = useRouter();

  const [events, setEvents] = useState<CalendarEventExtended[]>([]);
  const [loading, setLoading] = useState(false);
  const [pages, setPages] = useState(1);

  useEffect(() => {
    setLoading(true);

    getEvents()
      .then((result) => {
        setEvents(result.events || []);
        setPages(result.pages);
      })
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [getEvents, router.query.page]);

  useEffect(() => {
    if (pageState.calendarEvent) {
      const { calendarEvent } = pageState;

      const index = events.map((event) => event.id).indexOf(calendarEvent.id);
      const newEventsArray = [...events];
      newEventsArray.splice(index, 1, calendarEvent);

      setEvents(newEventsArray);
    }
  }, [pageState]);

  const handleEventClick = (event: CalendarEventExtended) => {
    if (!onEventClick) return null;
    onEventClick(event);
  };

  const handleUpdatePage = ({ page }: { page: number }) => {
    router.replace({ query: { ...router.query, page } });
  };

  const locale = language.split('-')[0];
  const eventsByMonth = {};
  events.forEach((event) => {
    const month = getFormattedDate({
      date: event.startDate,
      dateFormat: 'LLLL y',
      options: { locale: Locales[locale] }
    });

    if (!eventsByMonth[month]) eventsByMonth[month] = [];
    eventsByMonth[month].push(event);
  });

  return (
    <div className="list-view">
      {loading ? (
        <LoadingIndicator />
      ) : events.length ? (
        <div className="events">
          {Object.keys(eventsByMonth).map((month, i) => (
            <div className="month" key={month}>
              <div className="month-label">
                {month.charAt(0).toUpperCase() + month.slice(1)}
              </div>

              <div className="month-content">
                {eventsByMonth[month].map((event: CalendarEventExtended) => (
                  <div className="event" key={event.id}>
                    <CalendarEventCalendarListItem
                      event={event}
                      onEventClick={handleEventClick}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}

          <Pagination
            currentPage={Number(router.query.page) || 1}
            numberOfPages={pages}
            onPageChange={handleUpdatePage}
            style={{ marginBottom: 25 }}
          />
        </div>
      ) : (
        <EmptyIndicator
          icon={['far', 'calendar-days']}
          title={messages.eventCalendarEmpty}
        />
      )}

      <style jsx>{`
        .month {
          margin: 20px 20px 30px;
        }
        .month:not(:first-child) {
          margin-top: 40px;
        }
        .month-label {
          color: ${theme.textColorLight};
          font-size: 2rem;
          font-weight: 700;
          padding: 0 3px 15px;
        }
        .event:not(:first-child) {
          margin-top: 15px;
        }
      `}</style>
    </div>
  );
};

export default CalendarEventCalendarListView;
